// Hand-rolled replacement for redux-persist. redux-persist's entire
// async-rehydration protocol (a PersistGate loading screen, a persist/
// REHYDRATE action, a persistor object with its own lifecycle) exists to
// support storage backends that are asynchronous (AsyncStorage, IndexedDB).
// This app only ever targets `localStorage`, which is synchronous - so
// rehydration can happen once, directly, before the store is even created
// (see configureStore.js), and persistence is just "write the whitelisted
// slices to localStorage on every change." No loading screen, no dedicated
// persistor object, no rehydration action.
//
// Migration note: existing users have data under the OLD redux-persist key
// (`persist:root`), double-JSON-encoded per the library's own storage
// format. This deliberately does NOT read or migrate that old key - it is
// simplest and safe not to: the values this app persists are all a cache of
// data whose real source of truth is Firebase (portfolios/ledgers/settings
// are re-fetched on load; Firebase Auth's own session survives independently
// via the SDK's own IndexedDB-backed persistence, not this cache). Losing
// this cache once is, at worst, one extra login prompt and a brief refetch
// flash - a one-time, low-stakes cost, not a real migration.

type PersistableStore = {
  getState: () => Record<string, unknown>;
  subscribe: (listener: () => void) => () => void;
};

const STORAGE_KEY = 'myfolio:persisted-state';

// Debounced for most slices: a rapid burst of dispatches (e.g. typing in a
// form bound to Redux state) would otherwise write to localStorage on every
// keystroke. Auth is the exception - see persistState below.
const WRITE_DEBOUNCE_MS = 500;

const WHITELIST = [
  'auth',
  'portfoliosList',
  'portfolios',
  'settings',
  'ledgersList',
  'ledgers',
] as const;

type WhitelistKey = (typeof WHITELIST)[number];

type PersistedState = Partial<Record<WhitelistKey, unknown>>;

export const loadPersistedState = (): PersistedState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    // Corrupt JSON, or localStorage unavailable (e.g. private browsing in
    // some browsers throws on access) - persistence is a cache, not a
    // source of truth, so fall back to a clean slate rather than throwing.
    return {};
  }
};

export const clearPersistedState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // See loadPersistedState - localStorage access itself can throw.
  }
};

// Subscribes to the store and writes the whitelisted slices to localStorage.
// Auth changes are written immediately; everything else is debounced.
// Returns the unsubscribe function (mirrors store.subscribe's own return
// value), though nothing currently calls it - the subscription is meant to
// live for the app's entire lifetime, same as redux-persist's own
// persistStore() subscription did.
const writePersistedState = (state: Record<string, unknown>): void => {
  const toPersist: PersistedState = {};
  WHITELIST.forEach((key) => {
    toPersist[key] = state[key];
  });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // localStorage can throw when full or unavailable - persistence is
    // a cache, not a source of truth, so fail silently.
  }
};

export const persistState = (store: PersistableStore): (() => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastAuthSnapshot: string | null = null;

  return store.subscribe(() => {
    const state = store.getState();
    const authSnapshot = JSON.stringify(state.auth);
    const authChanged = authSnapshot !== lastAuthSnapshot;

    if (authChanged) {
      lastAuthSnapshot = authSnapshot;
      // Auth must land in localStorage synchronously on login/logout/signup.
      // Playwright e2e (and a real user hard-reloading within the debounce
      // window) re-create the Redux store from localStorage before Firebase
      // Auth's async onAuthStateChanged callback fires; if auth is still the
      // pre-login logged-out slice, AuthRoute bounces protected routes to
      // /auth/login before the session is restored.
      writePersistedState(state);
    }

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      lastAuthSnapshot = JSON.stringify(store.getState().auth);
      writePersistedState(store.getState());
    }, WRITE_DEBOUNCE_MS);
  });
};
